<template>
  <div class="tdt-map" ref="map" style="width: 100%; height: 800px"></div>
</template>

<script>
import loadTDT from "@/utils/loadTDT.js";

export default {
  name: "TdtMapFind",

  props: {
    tk: { type: String, required: true },
    center: {
      // [lng, lat]
      type: Array,
      default: () => [116.40769, 39.89945], // 默认中心：北京
    },
    zoom: { type: Number, default: 12 },
    // useImageBase: { type: Boolean, default: false }, // 是否使用影像底图
    markers: {
      // 标记点数组，data 用于生成模板
      type: Array,
      default: () => [],
      // 示例：[{ id: 1, lng: 121.33, lat: 30.74, data: { address: 'xxx', houseDescribe: 1 }}]
    },
  },

  data() {
    return {
      map: null,
      markerList: [],

      // 高亮相关
      highlightOverlay: null, // 当前高亮的 marker
      currentHighlightId: null, // 当前高亮点 id
      hiddenBaseMarker: null, // 被临时隐藏的普通 marker
      currentInfoWindow: null, // 当前打开的 InfoWindow

      // 初始化状态
      inited: false, // 地图是否初始化完成
      pendingMarkers: null, // 地图未就绪时暂存 markers

      // 映射：id -> marker
      markersMap: new Map(),
    };
  },

  async mounted() {
    const T = await loadTDT(this.tk);
    this.$nextTick(() => {
      this.initMap(T);
    });
  },

  methods: {
    /** 初始化地图与控件、事件 */
    initMap(T) {
      this.map = new T.Map(this.$refs.map, { projection: "EPSG:3857" });

      const center = new T.LngLat(this.center[0], this.center[1]);
      this.map.centerAndZoom(center, this.zoom);
      this.map.enableScrollWheelZoom();

      // 初始化控件
      this.map.addControl(new T.Control.Scale());
      this.map.addControl(new T.Control.Zoom());
      // this.map.addControl(new T.Control.MapType());

      // 初次加载 marker
      if (this.markers && this.markers.length) {
        this.addMarkers(T);
      }

      // 绑定事件
      this.map.addEventListener("click", (e) => {
        const { lng, lat } = e.lnglat;
        this.$emit("map-click", { lng, lat });
      });
      this.map.addEventListener("moveend", this.updateVisibleMarkers);
      this.map.addEventListener("zoomend", this.updateVisibleMarkers);
      this.$nextTick(() => this.updateVisibleMarkers());
    },

    /** 获取地图可视范围（南西角 & 北东角） */
    getMapBounds() {
      const bounds = this.map.getBounds();
      return {
        sw: bounds.getSouthWest(),
        ne: bounds.getNorthEast(),
      };
    },

    /** 高亮指定 id 的 marker（隐藏原点并显示 InfoWindow） */
    highlightMarker(id) {
      if (!this.map || !id) return;

      // 清理旧高亮与 InfoWindow
      this.resetMarkerIcon();

      const item = this.markers.find((m) => m.id === id);
      if (!item) return;

      // 找到并移除原基础 marker
      const baseMarker = this.markersMap.get(id);
      if (baseMarker) {
        this.map.removeOverLay(baseMarker);
        this.hiddenBaseMarker = baseMarker;
      }

      // 创建高亮 marker
      const point = new T.LngLat(item.lng, item.lat);
      const highlight = new T.Marker(point, {
        icon: new T.Icon({
          iconUrl: require("@/assets/locationMap4.png"),
          iconSize: new T.Point(42, 42),
          iconAnchor: new T.Point(21, 42),
        }),
      });

      // 添加高亮 marker
      this.map.addOverLay(highlight);
      this.highlightOverlay = highlight;
      this.currentHighlightId = id;

      // 创建并打开 InfoWindow
      const content = this.buildInfoWindowContent(item || {});
      const infoWindow = new T.InfoWindow(content, {
        offset: new T.Point(0, -20),
      });
      this.map.openInfoWindow(infoWindow, point);
      this.currentInfoWindow = infoWindow;

      //跳转详情
      this.bindInfoWindowTitleClick(item);

      // 三重保险确保高亮置顶
      try {
        highlight.bringToFront && highlight.bringToFront();
      } catch (e) {}
      try {
        highlight.setZIndexOffset && highlight.setZIndexOffset(999999);
      } catch (e) {}
      try {
        const el = highlight._icon || highlight._markerIcon;
        if (el) el.style.zIndex = 999999;
      } catch (e) {}

      // 可选：自动居中
      // this.map.panTo(point);
    },

    /** 恢复普通 marker，关闭高亮与 InfoWindow */
    resetMarkerIcon() {
      // 移除高亮覆盖物
      if (this.highlightOverlay) {
        this.map.removeOverLay(this.highlightOverlay);
        this.highlightOverlay = null;
      }

      // 关闭 InfoWindow
      if (this.currentInfoWindow) {
        this.map.closeInfoWindow(this.currentInfoWindow);
        this.currentInfoWindow = null;
      }

      // 恢复普通 marker
      if (this.hiddenBaseMarker) {
        this.map.addOverLay(this.hiddenBaseMarker);
        this.hiddenBaseMarker = null;
      }

      this.currentHighlightId = null;
    },

    /** 更新当前可视范围内的 markers 并抛出 */
    updateVisibleMarkers() {
      const bounds = this.getMapBounds();
      const visible = this.markers.filter(
        (item) =>
          item.lng >= bounds.sw.lng &&
          item.lng <= bounds.ne.lng &&
          item.lat >= bounds.sw.lat &&
          item.lat <= bounds.ne.lat
      );
      this.$emit("visible-update", visible);
    },

    /** 批量添加 marker */
    addMarkers(T) {
      // 清除旧 marker
      this.markerList.forEach((m) => this.map.removeOverLay(m));
      this.markerList = [];

      // 清空映射与高亮
      this.markersMap.clear();
      this.resetMarkerIcon();

      // 遍历添加新 marker
      this.markers.forEach((item) => {
        const point = new T.LngLat(item.lng, item.lat);

        let markerOpts = {};
        if (item.icon) {
          markerOpts.icon = new T.Icon({
            iconUrl: item.icon,
            iconSize: new T.Point(32, 32),
            iconAnchor: new T.Point(16, 32),
          });
        }

        const marker = new T.Marker(point, markerOpts);
        marker.__id = item.id;

        this.map.addOverLay(marker);
        this.markersMap.set(item.id, marker);

        // InfoWindow 绑定
        const content = this.buildInfoWindowContent(item || {});
        const infoWindow = new T.InfoWindow(content, {
          offset: new T.Point(0, -20),
        });
        marker.addEventListener("click", () => {
          this.map.openInfoWindow(infoWindow, point);

          this.bindInfoWindowTitleClick(item);
        });

        this.markerList.push(marker);
      });
    },

    /** 构造 InfoWindow 内容（HTML 字符串模板） */
    buildInfoWindowContent(item) {
      return `
        <div style="width:100%;display:flex;flex-direction:row;align-items:flex-start;">
          <!-- 左侧图片 -->
          <div style="flex:0 0 120px; margin-right:10px;margin-left:-6px">
            <img src="${item.housePic || "/images/default-house.png"}"
                 style="width:120px;height:120px;object-fit:cover;border-radius:4px;" />
          </div>

          <!-- 右侧文字内容 -->
          <div style="flex:1;display:flex;flex-direction:column;line-height:1.4;">
            <div
              class="tdt-info-title"
              style="
                color:#333;
                font-weight:bold;
                line-height:1.4;
                font-size:17px;
                cursor:pointer;
                margin-bottom:4px;
                transition:color 0.25s ease,text-shadow 0.25s ease;
              "
              onmouseover="
                this.style.color='#1677ff';
                this.style.textShadow='0 0 1px rgba(22,119,255,0.3)';
              "
              onmouseout="
                this.style.color='#333';
                this.style.textShadow='none';
              "
              onmousedown="
                this.style.color='#0958d9';
              "
              onmouseup="
                this.style.color='#1677ff';
              "
            >
              ${item.data.address || "未知地址"}
            </div>

            <div style="display:flex;flex-direction:row;margin:4px 0 2px 0;">
              <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">用途：</div>
              <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">
                ${item.data.leaseUseTypeText || "--"}
              </div>
            </div>

            <div style="display:flex;flex-direction:row;margin:2px 0;">
              <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">面积：</div>
              <div style="font-family:Microsoft YaHei;font-size:14px;color:#484848;">${
                item.data.rentCount || "--"
              }㎡</div>
            </div>

            <div style="display:flex;flex-direction:row;margin:2px 0;">
              <div style="font-family:Microsoft YaHei;font-size:14px;color: #f5222d;">价格：</div>
              <div style="font-family:Microsoft YaHei;font-size:14px;color: #f5222d;">${
                item.data.priceText || "--"
              }</div>
            </div>
          </div>
        </div>
      `;
    },

    bindInfoWindowTitleClick(item) {
      setTimeout(() => {
        const el = document.querySelector(".tdt-info-title");

        if (!el) return;

        el.onclick = () => {
          this.$emit("marker-title-click", item);
        };
      }, 0);
    },
  },

  watch: {
    /** 监听 markers 变化并重绘 */
    markers: {
      handler() {
        if (!this.map || !window.T) {
          setTimeout(() => {
            if (this.map && window.T) this.addMarkers(window.T);
          }, 300);
        } else {
          this.addMarkers(window.T);
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
